import { FormInstance } from 'antd';

export const mandatoryNationalAddressFields = [
  'postalCode', 'buildingNumber', 'additionalNumber', 'unitNumber',
  'streetName', 'districtName', 'fullAddress',
] as const;

const addressFields = [
  ...mandatoryNationalAddressFields,
  'merchantName', 'city', 'regionName', 'addressId', 'status',
] as const;

export type NationalAddress = Partial<Record<
  typeof addressFields[number] | 'street' | 'district' | 'address' | 'address2',
  string | null
>>;

export interface NationalAddressPayload {
  splAddress?: NationalAddress | null;
  nationalAddressLookup?: { status?: string; reviewRequired?: boolean };
  companyInformation?: {
    splAddress?: NationalAddress | null;
    nationalAddressReviewRequired?: boolean;
  };
  reviewCompanyInformation?: { nationalAddress?: NationalAddress | null };
}

export const savedSplAddress = (payload?: NationalAddressPayload) =>
  payload?.splAddress ?? payload?.companyInformation?.splAddress;

export const nationalAddressReviewRequired = (payload?: NationalAddressPayload) =>
  payload?.nationalAddressLookup?.reviewRequired === true ||
  payload?.companyInformation?.nationalAddressReviewRequired === true;

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const hasUsableNationalAddress = (address: unknown): address is NationalAddress => {
  if (!address || typeof address !== 'object' || Array.isArray(address)) return false;
  const data = address as NationalAddress;
  if (hasText(data.status) && data.status.toLowerCase() !== 'active') return false;
  return (['addressId', 'address', 'address2', 'fullAddress', 'city', 'street', 'district',
    'buildingNumber'] as const).some((field) => hasText(data[field])) ||
    (hasText(data.postalCode) && !/^0+$/.test(data.postalCode.trim()));
};

// Undefined is an uninitialised form field; null and blank strings are deliberate clears.
export const mergeNationalAddress = (
  spl?: NationalAddress | null,
  saved?: NationalAddress | null,
  current?: NationalAddress | null,
): NationalAddress => {
  const result: NationalAddress = {};
  addressFields.forEach((field) => {
    if (current === null) result[field] = null;
    else if (current?.[field] !== undefined) result[field] = current[field];
    else if (saved === null) result[field] = null;
    else if (saved?.[field] !== undefined) result[field] = saved[field];
    else {
      const alias = field === 'streetName' ? spl?.street :
        field === 'districtName' ? spl?.district : undefined;
      result[field] = spl?.[field] ?? alias;
    }
  });
  return result;
};

export const missingNationalAddressFields = (address?: NationalAddress | null) =>
  mandatoryNationalAddressFields.filter((field) => !hasText(address?.[field]));

// Wizard submission can call onFinish directly; do not rely only on Form.Item rules.
export const validateNationalAddressSubmission = async (
  form: FormInstance,
  address: NationalAddress | null | undefined,
  message: string,
): Promise<void> => {
  const errorFields = missingNationalAddressFields(address).map((field) => ({
    name: ['reviewCompanyInformation', 'nationalAddress', field],
    errors: [message],
  }));
  if (errorFields.length) {
    form.setFields(errorFields);
    form.scrollToField(errorFields[0].name);
    throw { errorFields, outOfDate: false };
  }
};
